import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import type { AdminParsedEvent } from '../../types';

const columns: Column<AdminParsedEvent>[] = [
  { key: 'event_timestamp', header: 'Timestamp', render: (r) =>
    new Date(r.event_timestamp).toLocaleString()
  },
  { key: 'activity_type_normalized', header: 'Activity', render: (r) => (
    <span className="capitalize">{r.activity_type_normalized}</span>
  )},
  { key: 'activity_category', header: 'Category', render: (r) => (
    <span className="capitalize">{r.activity_category}</span>
  )},
  { key: 'guard_name', header: 'Guard', render: (r) => r.guard_name || r.guard_name_raw },
  { key: 'site_name', header: 'Site' },
  { key: 'vendor_org_name', header: 'Vendor' },
  { key: 'is_incident', header: 'Incident', render: (r) =>
    r.is_incident ? <StatusBadge label="Yes" variant="error" /> : <span className="text-slate-400">No</span>
  },
  { key: 'ai_confidence', header: 'AI Conf.', render: (r) =>
    r.ai_confidence != null ? `${(r.ai_confidence * 100).toFixed(0)}%` : '—'
  },
];

export default function AdminMetrics() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'parsed-events', page, search, categoryFilter],
    queryFn: () => adminApi.getParsedEvents({
      page: String(page),
      search: search || undefined,
      activity_category: categoryFilter || undefined,
    }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Metrics" description={`${data?.count ?? 0} parsed events`} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Search events..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Categories</option>
          <option value="patrol">Patrol</option>
          <option value="incident">Incident</option>
          <option value="administrative">Administrative</option>
          <option value="attendance">Attendance</option>
        </select>
      </div>
      <DataTable
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(r) => r.id}
        isLoading={isLoading}
        emptyTitle="No parsed events"
        emptyDescription="Events appear after DARs are uploaded and processed."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
